import React, { useState, useRef, useEffect, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faKey, faSignOutAlt } from "@fortawesome/free-solid-svg-icons"
import { NavLink } from "react-router-dom"
import { APP_VERSION } from "../../utilities/Addons"
import AtelierModalCreeFicheMaster from "../atelier/AtelierModalCreeFicheMaster"
import { COLORS, NAV_HEIGHT } from "../../utilities/Constants"
import { useGroups } from "../../hooks/groups"
import { useRights } from "../../hooks/rights"
import { NAV_LAYOUT, resolveLayoutItem } from "../../config/navConfig"
import { fetchStockNonDebiteCount } from "../../store/actions/atelierInventaireActions"
import { fetchEntretiensSemaine } from "../../store/actions/entretienActions"
import { fetchDepACompleterCount } from "../../store/actions/depannageActions"
import NavNotifications from "./NavNotifications"

const navStyles = {
	navbar: {
		position: "sticky",
		top: 0,
		zIndex: 100,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		height: NAV_HEIGHT,
		// La bordure basse doit tenir DANS NAV_HEIGHT : sans border-box la nav
		// mesure 59px et les pages (calc(100vh - NAV_HEIGHT)) débordent d'1px.
		boxSizing: "border-box",
		padding: "0 24px",
		backgroundColor: COLORS.surface,
		borderBottom: `1px solid ${COLORS.border}`,
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
	},
	side: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		minWidth: 200,
	},
	brand: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	brandMark: {
		width: 26,
		height: 26,
		borderRadius: 8,
		backgroundColor: COLORS.text,
		color: "#fff",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 13,
		fontWeight: 700,
		flexShrink: 0,
	},
	brandName: {
		fontSize: 16,
		fontWeight: 600,
		letterSpacing: "-0.02em",
		color: COLORS.text,
	},
	version: {
		fontSize: 10,
		fontWeight: 500,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
	navList: {
		display: "flex",
		alignItems: "center",
		gap: 2,
		listStyle: "none",
		margin: 0,
		padding: 0,
	},
	navItem: {
		position: "relative",
		listStyle: "none",
	},
	navLink: {
		display: "flex",
		alignItems: "center",
		padding: "8px 14px",
		borderRadius: 8,
		fontSize: 14,
		fontWeight: 500,
		color: COLORS.textMuted,
		cursor: "pointer",
		textDecoration: "none",
		userSelect: "none",
		whiteSpace: "nowrap",
	},
	navLinkActive: {
		color: COLORS.text,
		backgroundColor: COLORS.bg,
	},
	caret: {
		marginLeft: 6,
		fontSize: 9,
		opacity: 0.5,
	},
	dropdownWrap: {
		position: "absolute",
		top: "100%",
		left: 0,
		paddingTop: 8,
		zIndex: 200,
	},
	// Menu du compte : ancré à droite, sinon il déborderait de la fenêtre.
	dropdownWrapRight: {
		position: "absolute",
		top: "100%",
		right: 0,
		paddingTop: 8,
		zIndex: 200,
	},
	dropdown: {
		minWidth: 210,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: 6,
		boxShadow: "0 10px 30px rgba(28, 25, 23, 0.10)",
		display: "flex",
		flexDirection: "column",
		gap: 2,
		listStyle: "none",
		margin: 0,
	},
	profile: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 12,
		minWidth: 200,
	},
	// Zone cliquable/survolable du compte (avatar + nom + caret).
	profileTrigger: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "4px 8px 4px 4px",
		borderRadius: 999,
		cursor: "pointer",
		userSelect: "none",
	},
	avatar: {
		width: 30,
		height: 30,
		borderRadius: 99,
		backgroundColor: COLORS.bg,
		border: `1px solid ${COLORS.border}`,
		color: COLORS.text,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 13,
		fontWeight: 600,
		flexShrink: 0,
	},
	userName: {
		fontSize: 14,
		fontWeight: 500,
		color: COLORS.text,
	},
}

const DropdownItem = ({
	icon,
	label,
	to,
	exact,
	disabled,
	locked,
	danger,
	onClick,
	badge = 0,
}) => {
	const [hover, setHover] = useState(false)
	// `locked` = bloqué par le système de fermeture de fiche (fiche ouverte ailleurs) :
	// rendu en rouge ; `disabled` = simplement indisponible : gris. Les deux empêchent
	// la navigation et affichent le curseur « interdit ». `danger` = action
	// destructrice mais bien cliquable (déconnexion) : rouge sans blocage.
	const blocked = disabled || locked
	const base = {
		display: "flex",
		alignItems: "center",
		gap: 11,
		padding: "9px 12px",
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		color:
			locked || danger
				? COLORS.danger
				: disabled
					? COLORS.textLight
					: COLORS.text,
		textDecoration: "none",
		cursor: blocked ? "not-allowed" : "pointer",
		backgroundColor:
			hover && !blocked
				? danger
					? COLORS.dangerLight
					: COLORS.bg
				: "transparent",
		transition: "background-color 0.12s",
		whiteSpace: "nowrap",
		fontFamily: "inherit",
		border: "none",
		width: "100%",
		textAlign: "left",
		boxSizing: "border-box",
	}
	const iconStyle = {
		width: 16,
		fontSize: 13,
		color:
			locked || danger
				? COLORS.danger
				: disabled
					? COLORS.textLight
					: COLORS.textMuted,
		flexShrink: 0,
	}
	const badgeEl = badge > 0 && (
		<span
			style={{
				marginLeft: "auto",
				minWidth: 18,
				height: 18,
				padding: "0 5px",
				borderRadius: 999,
				backgroundColor: COLORS.danger,
				color: "#fff",
				fontSize: 11,
				fontWeight: 700,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{badge}
		</span>
	)
	const content = (
		<>
			<FontAwesomeIcon icon={icon} style={iconStyle} />
			<span>{label}</span>
			{badgeEl}
		</>
	)
	if (onClick) {
		return (
			<button
				type="button"
				style={base}
				onClick={onClick}
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
			>
				{content}
			</button>
		)
	}
	return (
		<NavLink
			to={to}
			exact={exact}
			style={base}
			activeStyle={
				blocked
					? undefined
					: {
							backgroundColor: COLORS.accentLight,
							color: COLORS.accent,
						}
			}
			onClick={blocked ? (e) => e.preventDefault() : undefined}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{content}
		</NavLink>
	)
}

const NavGroup = ({ label, isOpen, onOpen, onClose, children, badge = 0 }) => (
	<li style={navStyles.navItem} onMouseEnter={onOpen} onMouseLeave={onClose}>
		<div
			style={{
				...navStyles.navLink,
				...(isOpen ? navStyles.navLinkActive : {}),
			}}
		>
			{label}
			{badge > 0 && (
				<span
					style={{
						marginLeft: 6,
						minWidth: 18,
						height: 18,
						padding: "0 5px",
						borderRadius: 999,
						backgroundColor: COLORS.danger,
						color: "#fff",
						fontSize: 11,
						fontWeight: 700,
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{badge}
				</span>
			)}
			<span style={navStyles.caret}>▾</span>
		</div>
		{isOpen && (
			<div style={navStyles.dropdownWrap}>
				<ul style={navStyles.dropdown}>{children}</ul>
			</div>
		)}
	</li>
)

// Item de nav cliquable direct (pas de dropdown).
const NavDirect = ({ label, to, badge = 0 }) => {
	const [hover, setHover] = useState(false)
	return (
		<li style={navStyles.navItem}>
			<NavLink
				to={to}
				style={{
					...navStyles.navLink,
					...(hover ? navStyles.navLinkActive : {}),
				}}
				activeStyle={navStyles.navLinkActive}
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
			>
				{label}
				{badge > 0 && (
					<span
						style={{
							marginLeft: 6,
							minWidth: 18,
							height: 18,
							padding: "0 5px",
							borderRadius: 999,
							backgroundColor: COLORS.danger,
							color: "#fff",
							fontSize: 11,
							fontWeight: 700,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{badge}
					</span>
				)}
			</NavLink>
		</li>
	)
}

// Menu « compte » : l'avatar + le nom ouvrent les options du compte
// (mot de passe, déconnexion) au lieu de déconnecter au clic.
const AccountMenu = ({ initial, name, isOpen, onOpen, onClose, onLogout }) => (
	<div
		style={{ position: "relative" }}
		onMouseEnter={onOpen}
		onMouseLeave={onClose}
	>
		<div
			style={{
				...navStyles.profileTrigger,
				backgroundColor: isOpen ? COLORS.bg : "transparent",
			}}
		>
			<div style={navStyles.avatar}>{initial}</div>
			<span style={navStyles.userName}>{name}</span>
			<span style={{ ...navStyles.caret, marginLeft: 0 }}>▾</span>
		</div>
		{isOpen && (
			<div style={navStyles.dropdownWrapRight}>
				<ul style={navStyles.dropdown}>
					<DropdownItem
						icon={faKey}
						label="Changer de mot de passe"
						to="/compte/mot-de-passe"
					/>
					<DropdownItem
						icon={faSignOutAlt}
						label="Se déconnecter"
						danger
						onClick={onLogout}
					/>
				</ul>
			</div>
		)}
	</div>
)

// Résout un item de config (top-bar) → props de DropdownItem. Les comportements
// dynamiques (fiche ouverte, disabled, modal master) vivent ici, la structure
// dans navConfig.
const navItemProps = (item, ctx) => {
	const badge = item.badgeKey ? ctx.badges[item.badgeKey] || 0 : 0
	const base = {
		icon: item.icon,
		label: item.label,
		exact: item.exact,
		badge,
	}
	switch (item.kind) {
		case "fiche": {
			const f = ctx.fiche[item.domain]
			return {
				...base,
				to: f.open ? `${f.route}/${f.id}` : "#",
				disabled: !f.open,
			}
		}
		case "master":
			return {
				...base,
				label: `Fiche ${ctx.masterLabel}`,
				onClick: ctx.openMaster,
			}
		case "passage":
			// Même destination que la card du dashboard : fiche vierge pré-remplie
			// client de passage, résolue au mount d'AtelierV2 (`location.from`).
			return {
				...base,
				to: { pathname: "/atelier/fiche/0", from: "client-passage" },
			}
		default:
			return { ...base, to: item.to }
	}
}

const BADGE_POLL_MS = 5 * 60 * 1000
const BADGE_FOCUS_GUARD_MS = 30 * 1000

const NavV2 = ({ loginDisconnect }) => {
	const dispatch = useDispatch()
	const { hasRight } = useRights()
	const { username } = useSelector((state) => state.navInfo.login)
	const { atelierInfo } = useSelector((state) => state)
	const { atelier } = atelierInfo
	const { location } = useSelector((state) => state.locationInfo)
	const { dep } = useSelector((state) => state.depInfo)
	const { masterEntite } = useSelector((state) => state.masterReducer)
	const { entretiensRetard } = useSelector((state) => state.entretienInfo)
	const { stockNonDebite } = useSelector((state) => state.atelierInventaire)
	const { depACompleterCount } = useSelector((state) => state.depInfo)
	const { notifCategories } = useGroups()
	const [showNewFicheMasterModal, setShowNewFicheMasterModal] =
		useState(false)
	const [openMenu, setOpenMenu] = useState(null)
	// Intention de survol : on ne ferme pas immédiatement au mouseLeave du bouton.
	// En descendant vers le menu déroulant, la souris peut sortir brièvement de la
	// zone survolée (mouvement rapide/diagonal, écart d'1px selon l'écran/zoom) —
	// une fermeture immédiate faisait « disparaître » le menu avant qu'on l'atteigne.
	// Un délai court, annulé dès qu'on re-rentre (bouton OU menu), évite ça.
	const CLOSE_DELAY_MS = 140
	const closeTimer = useRef(null)
	const cancelClose = () => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current)
			closeTimer.current = null
		}
	}
	const open = (key) => () => {
		cancelClose()
		setOpenMenu(key)
	}
	const close = () => {
		cancelClose()
		closeTimer.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY_MS)
	}
	// Nettoyage du timer en attente au démontage.
	useEffect(() => cancelClose, [])

	const lastBadgeFetch = useRef(0)
	const refreshBadges = useCallback(() => {
		const now = Date.now()
		if (now - lastBadgeFetch.current < BADGE_FOCUS_GUARD_MS) return
		lastBadgeFetch.current = now
		dispatch(fetchStockNonDebiteCount())
		dispatch(fetchEntretiensSemaine({ silent: true }))
		dispatch(fetchDepACompleterCount())
	}, [dispatch])

	useEffect(() => {
		const id = setInterval(refreshBadges, BADGE_POLL_MS)
		const onFocus = () => refreshBadges()
		window.addEventListener("focus", onFocus)
		return () => {
			clearInterval(id)
			window.removeEventListener("focus", onFocus)
		}
	}, [refreshBadges])

	// Bouton « Fiche » = DERNIÈRE FICHE VISITÉE de la session : dégrisé dès que
	// le slice du domaine porte un id, et le clic y ramène (ce qui la
	// re-verrouille au montage). Aucune persistance : login et F5 repartent
	// grisés. Un seul prédicat pour les deux sentinelles qui cohabitent —
	// atelier/location posent `null` sur une fiche neuve, dépannage pose 0
	// (`getBaseDep()`).
	const ficheMemorisee = (id) => id !== null && id !== undefined && id > 0
	const ficheAtelierMemorisee = ficheMemorisee(atelier.id)
	const ficheLocationMemorisee = ficheMemorisee(location.id)
	const ficheDepMemorisee = ficheMemorisee(dep.id)
	const getUsername = () => {
		if (username.length > 0)
			return `${username[0].toUpperCase()}${username.slice(1)}`
		return ""
	}
	// Contexte passé au résolveur d'items (état dynamique + callbacks).
	const ctx = {
		fiche: {
			dep: {
				open: ficheDepMemorisee,
				id: dep.id,
				route: "/depannage/fiche",
			},
			ate: {
				open: ficheAtelierMemorisee,
				id: atelier.id,
				route: "/atelier/fiche",
			},
			loc: {
				open: ficheLocationMemorisee,
				id: location.id,
				route: "/location/fiche",
			},
		},
		masterLabel: masterEntite.label || "SVP",
		openMaster: () => {
			setShowNewFicheMasterModal(true)
			close()
		},
		badges: {
			entretiensRetard,
			stockNonDebite,
			depACompleter: depACompleterCount,
		},
	}
	return (
		<>
			{showNewFicheMasterModal && (
				<AtelierModalCreeFicheMaster
					visible={showNewFicheMasterModal}
					onClose={() => setShowNewFicheMasterModal(false)}
				/>
			)}
			<nav style={navStyles.navbar}>
				<div style={navStyles.side}>
					<div style={navStyles.brand}>
						<span style={navStyles.brandMark}>S</span>
						<span style={navStyles.brandName}>SvpApp</span>
					</div>
					{hasRight("patchnotes") ? (
						<NavLink
							to="/patchnotes"
							style={{
								...navStyles.version,
								textDecoration: "none",
								cursor: "pointer",
							}}
							activeStyle={{ color: COLORS.accent }}
						>
							{`v${APP_VERSION}`}
						</NavLink>
					) : (
						<span
							style={navStyles.version}
						>{`v${APP_VERSION}`}</span>
					)}
				</div>

				<ul style={navStyles.navList}>
					{NAV_LAYOUT.map((entry) => {
						if (entry.type === "direct") {
							const item = resolveLayoutItem(entry.key)
							if (item.right && !hasRight(item.right)) return null
							return (
								<NavDirect
									key={entry.key}
									label={item.label}
									to={item.to}
									badge={
										item.badgeKey
											? ctx.badges[item.badgeKey]
											: 0
									}
								/>
							)
						}
						const items = entry.items
							.map(resolveLayoutItem)
							.filter((it) => !it.right || hasRight(it.right))
						if (items.length === 0) return null
						const groupBadge = items.reduce(
							(sum, it) =>
								sum +
								(it.badgeKey
									? ctx.badges[it.badgeKey] || 0
									: 0),
							0,
						)
						return (
							<NavGroup
								key={entry.key}
								label={entry.label}
								isOpen={openMenu === entry.key}
								onOpen={open(entry.key)}
								onClose={close}
								badge={groupBadge}
							>
								{items.map((it) => (
									<DropdownItem
										key={it.key}
										{...navItemProps(it, ctx)}
									/>
								))}
							</NavGroup>
						)
					})}
				</ul>

				<div style={navStyles.profile}>
					{notifCategories.length > 0 && <NavNotifications />}
					<AccountMenu
						initial={getUsername().charAt(0) || "?"}
						name={getUsername()}
						isOpen={openMenu === "__compte"}
						onOpen={open("__compte")}
						onClose={close}
						onLogout={loginDisconnect}
					/>
				</div>
			</nav>
		</>
	)
}

export default NavV2
